"use client"; // Diperlukan untuk form dan komponen interaktif

import ApiErrorParser from "@/helper/ApiErrorParser";
import Api from "@/utils/Api";
import { Email, LocationOn, Phone, Send } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import type { NextPage } from "next";
import React, { useState } from "react";
import * as Yup from "yup";

const PageHeader: React.FC = () => (
  <Box className="text-center py-12 md:py-16 bg-slate-50">
    <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
      Kami Siap Membantu Anda
    </Typography>
    <Typography variant="h6" component="p" className="text-gray-600 mt-2">
      Punya pertanyaan atau butuh bantuan? Jangan ragu untuk menghubungi kami.
    </Typography>
  </Box>
);

const ContactInfo: React.FC = () => (
  <Paper elevation={3} className="p-6 md:p-8 h-full rounded-lg">
    <Typography
      variant="h5"
      component="h2"
      className="font-semibold text-left"
      gutterBottom
    >
      Informasi Kontak
    </Typography>
    <Typography variant="body1" className="text-gray-600 mb-6">
      Hubungi kami melalui detail di bawah ini. Tim kami akan segera merespons
      Anda.
    </Typography>
    <List>
      <ListItem disablePadding>
        <ListItemIcon>
          <Email color="primary" />
        </ListItemIcon>
        <ListItemText
          primary="support@umkms.com"
          secondary="Untuk Pertanyaan Umum"
        />
      </ListItem>
      <ListItem disablePadding className="mt-4">
        <ListItemIcon>
          <Phone color="primary" />
        </ListItemIcon>
        <ListItemText
          primary="0821-2349-6617"
          secondary="Senin - Jumat, 09:00 - 18:00"
        />
      </ListItem>
      <ListItem disablePadding className="mt-4">
        <ListItemIcon>
          <LocationOn color="primary" />
        </ListItemIcon>
        <ListItemText
          primary="PT UMKM Pintar Indonesia"
          secondary="Gedung Artha Graha Lt. 9 Sudirman Central Business District Lot 25., Jl. Jend. Sudirman Kav. 52-53, Jakarta Selatan - 12190"
        />
      </ListItem>
    </List>
    {/* Peta Lokasi Interaktif */}
    <Box
      className="mt-8 rounded-lg overflow-hidden h-80"
      sx={{ border: "1px solid #ddd" }}
    >
      <iframe
        src="https://www.google.com/maps/embed/v1/place?q=gedung+artha+graha&key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
        style={{ border: 0, width: "100%", height: "100%" }}
        allowFullScreen={false}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Peta Lokasi PT UMKM Pintar Indonesia"
      ></iframe>
    </Box>
  </Paper>
);

const ContactForm: React.FC = () => {
  const validationSchema = Yup.object({
    companyName: Yup.string().required("Nama Perusahaan harus diisi"),
    picName: Yup.string().required("Nama PIC harus diisi"),
    email: Yup.string()
      .email("Alamat email tidak valid")
      .required("Alamat email harus diisi"),
    phoneNumber: Yup.string()
      .required("Nomor telepon harus diisi")
      .matches(/^(\+62|0)(8[1-9][0-9]{7,10})$/g, "Nomor telepon tidak valid"),
    message: Yup.string()
      .required("Pesan harus diisi")
      .max(255, "Pesan tidak boleh lebih dari 255 karakter"),
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  return (
    <Formik
      initialValues={{
        companyName: "",
        picName: "",
        email: "",
        phoneNumber: "",
        message: "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        await Api.post("/user/contact-support", values)
          .then(() => {
            setSnackbarOpen(true);
            setSnackbarMessage("Pesan berhasil dikirimkan");
            setSnackbarSeverity("success");
            setSubmitting(false);
            resetForm();
          })
          .catch((error) => {
            setSnackbarOpen(true);
            setSnackbarMessage(
              error instanceof Error
                ? new ApiErrorParser(error).toString()
                : "Terjadi kesalahan"
            );
            setSnackbarSeverity("error");
            setSubmitting(false);
          });
      }}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <Form noValidate autoComplete="off">
          <Paper elevation={3} className="p-6 md:p-8 h-full rounded-lg">
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600 }}
              gutterBottom
            >
              Kirim Pesan Langsung
            </Typography>
            <Box className="mt-6">
              <Grid container spacing={3} direction={"column"}>
                <Grid>
                  <Field
                    fullWidth
                    required
                    label="Nama Perusahaan"
                    variant="outlined"
                    name="companyName"
                    type="text"
                    as={TextField}
                  />
                  <ErrorMessage
                    name="companyName"
                    component="div"
                    className="text-red-600 text-xs"
                  />
                </Grid>
                <Grid>
                  <Field
                    fullWidth
                    required
                    label="Nama PIC"
                    variant="outlined"
                    name="picName"
                    type="text"
                    as={TextField}
                  />
                  <ErrorMessage
                    name="picName"
                    component="div"
                    className="text-red-600 text-xs"
                  />
                </Grid>
                <Grid>
                  <Field
                    fullWidth
                    required
                    type="email"
                    label="Alamat Email"
                    variant="outlined"
                    name="email"
                    as={TextField}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-600 text-xs"
                  />
                </Grid>
                <Grid>
                  <Field
                    fullWidth
                    required
                    type="tel"
                    label="Nomor Telepon"
                    variant="outlined"
                    name="phoneNumber"
                    as={TextField}
                    inputProps={{ maxLength: 15 }}
                  />
                  <ErrorMessage
                    name="phoneNumber"
                    component="div"
                    className="text-red-600 text-xs"
                  />
                </Grid>
                <Grid>
                  <Field
                    fullWidth
                    required
                    label="Pesan Anda"
                    variant="outlined"
                    name="message"
                    type="text"
                    as={TextField}
                    multiline
                    rows={5}
                    inputProps={{
                      maxLength: 255,
                      placeholder: "Masukkan Pesan Anda",
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <ErrorMessage
                    name="message"
                    component="div"
                    className="text-red-600 text-xs"
                  />
                </Grid>
                <Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Send />
                      )
                    }
                    sx={{ py: 1.5, fontWeight: 600 }}
                    disabled={!(isValid && dirty) || isSubmitting}
                  >
                    Kirim Pesan
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={5000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              variant="filled"
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Form>
      )}
    </Formik>
  );
};

export const ContactUsSection: NextPage = () => {
  return (
    <Box>
      <PageHeader />
      <Box component="main" className="py-4 md:py-8">
        <Container maxWidth="lg">
          <Grid container spacing={5} sx={{ justifyContent: "center" }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box>
                <ContactInfo />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box>
                <ContactForm />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};
